/**
 * Movimento: até onde uma unidade chega, por onde, e onde é perigoso pisar.
 *
 * Três perguntas, três algoritmos. O alcance é uma busca de Dijkstra a partir
 * da unidade (o custo varia por terreno, então não serve busca em largura); o
 * caminho é um A*; e a zona de ameaça é a união dos alcances de ataque de todos
 * os inimigos — a informação que decide toda jogada num Fire Emblem.
 */
import { isTeleportStep, moveCost, moveNeighbors, occupantAt, tilesInRangeOf, } from './grid.js';
import { distance, footprint, positionKey, } from './types.js';
/**
 * Fila de prioridade mínima sobre um heap binário.
 *
 * Um vetor ordenado a cada inserção funcionaria, e foi o primeiro impulso; num
 * mapa 40×40 com movimento 7 isso vira dezenas de milhares de reordenações por
 * turno, e a IA da Etapa 4 chama esta função centenas de vezes por decisão.
 */
class MinHeap {
    #items = [];
    get size() {
        return this.#items.length;
    }
    push(key, value) {
        const items = this.#items;
        items.push({ key, value });
        let i = items.length - 1;
        while (i > 0) {
            const parent = (i - 1) >> 1;
            if (items[parent].key <= items[i].key)
                break;
            [items[parent], items[i]] = [items[i], items[parent]];
            i = parent;
        }
    }
    pop() {
        const items = this.#items;
        const top = items[0];
        if (top === undefined)
            return undefined;
        const last = items.pop();
        if (items.length > 0) {
            items[0] = last;
            let i = 0;
            for (;;) {
                const left = i * 2 + 1;
                const right = left + 1;
                let smallest = i;
                if (left < items.length && items[left].key < items[smallest].key)
                    smallest = left;
                if (right < items.length && items[right].key < items[smallest].key)
                    smallest = right;
                if (smallest === i)
                    break;
                [items[smallest], items[i]] = [items[i], items[smallest]];
                i = smallest;
            }
        }
        return top;
    }
}
/** Até onde a unidade chega com o movimento que tem. */
export function reachableTiles(map, content, occupancy, start, movement, team, size) {
    const tiles = new Map();
    const cameFrom = new Map();
    const best = new Map();
    const startKey = positionKey(start);
    best.set(startKey, 0);
    tiles.set(startKey, { position: start, cost: 0 });
    const frontier = new MinHeap();
    frontier.push(0, start);
    while (frontier.size > 0) {
        const current = frontier.pop();
        const currentCost = current.key;
        const at = current.value;
        // Entrada obsoleta: já chegamos aqui mais barato.
        if (currentCost > (best.get(positionKey(at)) ?? Infinity))
            continue;
        for (const next of moveNeighbors(map, at)) {
            const step = moveCost(map, content, occupancy, team, next, size);
            if (step === null)
                continue;
            const cost = currentCost + step;
            if (cost > movement)
                continue;
            const key = positionKey(next);
            if (cost < (best.get(key) ?? Infinity)) {
                best.set(key, cost);
                tiles.set(key, { position: next, cost });
                cameFrom.set(key, at);
                frontier.push(cost, next);
            }
        }
    }
    return { tiles, cameFrom };
}
/**
 * Custo de viagem de `from` até cada casa do mapa, sem teto de movimento.
 *
 * A distância de Manhattan mente em mapa fechado: dois pontos separados por
 * uma parede ficam "a três casas" um do outro, e uma IA que se aproxima por
 * ela anda até encostar no muro e para ali para sempre. Este campo responde a
 * pergunta certa — quanto custa de fato chegar — e enxerga os teleportes, que
 * é o que torna transitáveis os mapas partidos do acervo.
 *
 * As unidades são ignoradas de propósito: quem está no caminho hoje sai amanhã,
 * e uma rota descartada por causa de um aliado parado seria uma rota perdida
 * por nada.
 */
export function travelField(map, content, from, team, size) {
    const empty = new Map();
    const best = new Map([[positionKey(from), 0]]);
    const frontier = new MinHeap();
    frontier.push(0, from);
    while (frontier.size > 0) {
        const current = frontier.pop();
        const at = current.value;
        if (current.key > (best.get(positionKey(at)) ?? Infinity))
            continue;
        for (const next of moveNeighbors(map, at)) {
            const step = moveCost(map, content, empty, team, next, size);
            if (step === null)
                continue;
            const cost = current.key + step;
            const key = positionKey(next);
            if (cost < (best.get(key) ?? Infinity)) {
                best.set(key, cost);
                frontier.push(cost, next);
            }
        }
    }
    return best;
}
/**
 * Onde a unidade pode PARAR.
 *
 * Diferente de onde ela pode passar: atravessar um aliado é permitido, terminar
 * o movimento em cima dele não.
 */
export function standableTiles(range, occupancy, selfId, size) {
    const out = [];
    for (const tile of range.tiles.values()) {
        // Um corpo grande precisa da área inteira livre para PARAR: atravessou o
        // aliado, mas não termina a jogada em cima dele.
        const clear = footprint(tile.position, size).every((p) => {
            const occupant = occupantAt(occupancy, p);
            return !occupant || occupant.id === selfId;
        });
        if (clear)
            out.push(tile.position);
    }
    return out;
}
/** Caminho da origem até o destino, reconstruído do alcance já calculado. */
export function pathTo(range, destination) {
    const destKey = positionKey(destination);
    if (!range.tiles.has(destKey))
        return null;
    const path = [destination];
    let key = destKey;
    // O limite é a proteção contra ciclo: um cameFrom corrompido não pode
    // travar o servidor num laço infinito.
    for (let guard = 0; guard < 10_000; guard++) {
        const previous = range.cameFrom.get(key);
        if (!previous)
            break;
        path.push(previous);
        key = positionKey(previous);
    }
    return path.reverse();
}
/** Custo total de um caminho, ou `null` se algum passo for ilegal. */
export function pathCost(map, content, occupancy, team, path, size) {
    if (path.length === 0)
        return null;
    let total = 0;
    for (let i = 1; i < path.length; i++) {
        const from = path[i - 1];
        const to = path[i];
        // Um passo é para a casa ao lado — ou para o outro lado de um teleporte,
        // que é a única forma de dois pontos distantes serem vizinhos no grafo.
        if (distance(from, to) !== 1 && !isTeleportStep(map, from, to))
            return null;
        const step = moveCost(map, content, occupancy, team, to, size);
        if (step === null)
            return null;
        total += step;
    }
    return total;
}
/** Casas que a unidade consegue atacar, considerando mover-se antes. */
export function attackableTiles(range, occupancy, selfId, minRange, maxRange, size) {
    const seen = new Set();
    const out = [];
    for (const from of standableTiles(range, occupancy, selfId, size)) {
        for (const target of tilesInRangeOf(from, size, minRange, maxRange)) {
            const key = positionKey(target);
            if (!seen.has(key)) {
                seen.add(key);
                out.push(target);
            }
        }
    }
    return out;
}
/**
 * Zona de ameaça: toda casa onde uma unidade hostil pode atingir você.
 *
 * O cálculo ignora a ocupação das unidades da equipe ameaçada de propósito —
 * um inimigo alcança aquela casa assim que você sair de perto, e mostrar a
 * ameaça só onde há caminho livre agora daria uma sensação falsa de segurança.
 */
export function threatZone(map, content, units, against) {
    const playerSide = (t) => t === 'jogador' || t === 'aliado';
    const hostiles = units.filter((u) => u.alive && u.team !== 'neutro' && playerSide(u.team) !== playerSide(against));
    // Ninguém bloqueia este cálculo, e é intencional. As unidades ameaçadas não
    // podem servir de muro — sair de perto libera a passagem, e uma ameaça que
    // some porque um aliado está no caminho é justamente a que mata. Entre si os
    // hostis já se atravessam, por serem do mesmo lado.
    const occupancy = new Map();
    const zone = new Set();
    for (const hostile of hostiles) {
        const range = reachableTiles(map, content, occupancy, hostile.position, hostile.movement, hostile.team, hostile.size);
        for (const tile of range.tiles.values()) {
            for (const target of tilesInRangeOf(tile.position, hostile.size, hostile.minRange, hostile.maxRange)) {
                zone.add(positionKey(target));
            }
        }
    }
    return zone;
}
/**
 * O alcance de um hostil, como quem joga precisa VER: onde ele chega a pé e o
 * que ele atinge de lá, numa mancha só.
 *
 * Não é a pergunta de `threatZone`, apesar da semelhança. Aquela responde
 * "onde eu levo dano", e por isso deixa de fora a casa em que um arqueiro
 * para: encostar nele é seguro. Esta responde "até onde este inimigo manda",
 * que é a leitura de quem escolhe onde parar — a casa que o arqueiro vai
 * ocupar está tomada do mesmo jeito, mesmo que ele não bata nela.
 *
 * O que cai fora da grade é descartado: o alcance de arma de quem está na
 * borda transborda o mapa, e o que transborda não se desenha.
 */
export function hostileReach(map, content, units) {
    // Ninguém bloqueia, pelo mesmo motivo de `threatZone`: quem fecha a
    // passagem hoje sai de lá amanhã, e a ameaça que some por causa de um
    // aliado parado é justamente a que mata.
    const occupancy = new Map();
    const seen = new Set();
    const out = [];
    const keep = (p) => {
        if (p.x < 0 || p.y < 0 || p.x >= map.width || p.y >= map.height)
            return;
        const key = positionKey(p);
        if (seen.has(key))
            return;
        seen.add(key);
        out.push(p);
    };
    for (const unit of units) {
        if (!unit.alive)
            continue;
        const range = reachableTiles(map, content, occupancy, unit.position, unit.movement, unit.team, unit.size);
        for (const tile of range.tiles.values()) {
            // O CORPO inteiro, e não só a âncora: um dragão de duas casas por duas
            // ocupa quatro, e a franja de baixo e da direita não aparece no alcance
            // de arma — ela fica à distância zero dele, e arma nenhuma começa em
            // zero. Sem isto, a mancha deixava buracos justamente onde a criatura
            // grande cabe.
            for (const body of footprint(tile.position, unit.size))
                keep(body);
            for (const target of tilesInRangeOf(tile.position, unit.size, unit.minRange, unit.maxRange)) {
                keep(target);
            }
        }
    }
    return out;
}
//# sourceMappingURL=movement.js.map